import React, { useMemo, useState } from "react";
import { Button, Collapse, Input, Popover } from "antd";
import { SnippetsOutlined } from "@ant-design/icons";
import { useDialog } from "./DialogProvider";
import "./SyncStrava.css";

/**
 * Reusable Strava sync UI.
 *
 * - Controlled input: parent owns `code` and `setCode`
 * - Backend call: uses passed `axiosInstance` to POST `/strava_sync/`
 * - Persists dummy token marker into `otherUserInfo` via `setOtherUserInfo`
 */
export default function SyncStrava({
  axiosInstance,
  otherUserInfo,
  setOtherUserInfo,
  code,
  setCode,
  headerWidth = "100%",
  headerBackground = "#fd824e",
  contentBackground = "rgba(255, 255, 255, 0.52)",
  onCodeNonEmptyChange,
}) {
  const { alert: alertDlg } = useDialog();
  const [isConnecting, setIsConnecting] = useState(false);

  const isStravaConnected = useMemo(() => {
    const token = otherUserInfo?.stravaRefreshToken;
    return !!(token && token !== "invalid_refresh_token");
  }, [otherUserInfo?.stravaRefreshToken]);

  const pasteFromClipboard = async () => {
    try {
      if (!navigator?.clipboard?.readText) {
        alertDlg("Clipboard access isn't available here. Please paste the Strava code manually (Cmd/Ctrl+V).");
        return;
      }
      const text = (await navigator.clipboard.readText()) || "";
      const v = text.trim();
      if (!v) return;
      setCode(v);
      onCodeNonEmptyChange && onCodeNonEmptyChange(true);
    } catch (e) {
      alertDlg("Could not read from clipboard. Please paste the Strava code manually (Cmd/Ctrl+V).");
    }
  };

  const connectStrava = async () => {
    const v = String(code || "").trim();
    if (!v) return;
    if (!axiosInstance) {
      alertDlg("Strava sync is not available (missing axios instance).");
      return;
    }
    try {
      setIsConnecting(true);
      const resp = await axiosInstance.post("/strava_sync/", { strava_access_code: v });
      const token = resp.data?.strava_refresh_token;
      if (token) {
        setOtherUserInfo && setOtherUserInfo({ ...(otherUserInfo || {}), stravaRefreshToken: token });
      }
      if (token && token !== "invalid_refresh_token") {
        alertDlg("Strava account connected.");
        setCode("");
        onCodeNonEmptyChange && onCodeNonEmptyChange(false);
      } else {
        alertDlg(resp.data?.strava_error || "Failed to connect to Strava.");
      }
    } catch (e) {
      alertDlg("Failed to connect to Strava.");
    } finally {
      setIsConnecting(false);
    }
  };

  const disabled = String(code || "").trim() === "";

  return (
    <Collapse
      className="sync-strava"
      size="small"
      style={{
        width: headerWidth,
        ["--sync-strava-header-bg"]: headerBackground,
        ["--sync-strava-content-bg"]: contentBackground,
      }}
      items={[
        {
          key: "strava",
          label: (
            <span style={{ color: "black", fontWeight: 600 }}>
              {isStravaConnected ? "Well connected to Strava ✓" : "Sync Strava first"}
            </span>
          ),
          children: (
            <>
              <div style={{ marginBottom: 8 }}>
                Click{" "}
                <a
                  href="https://www.strava.com/oauth/authorize?client_id=128363&response_type=code&redirect_uri=https%3A%2F%2Fsimplyride.app%2Fapi%2Fdisplay_code%2F&scope=activity%3Awrite"
                  rel="noreferrer"
                  target="_blank"
                >
                  here
                </a>{" "}
                and follow the instructions to (re) sync your account
              </div>

              <Input
                value={code}
                placeholder={isStravaConnected ? "Paste a new Strava code to update" : "Paste your access code here"}
                suffix={
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Paste Strava code"
                    title="Paste from clipboard"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={pasteFromClipboard}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        pasteFromClipboard();
                      }
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "rgba(15, 23, 42, 0.70)",
                    }}
                  >
                    <SnippetsOutlined />
                  </span>
                }
                onChange={(e) => {
                  const next = e.target.value;
                  setCode(next);
                  onCodeNonEmptyChange && onCodeNonEmptyChange(String(next || "").trim() !== "");
                }}
              />

              <div style={{ marginTop: 8 }}>
                {disabled ? (
                  <Popover content="Paste the Strava code first">
                    <Button disabled className="glass-cta" type="default">
                      {isStravaConnected ? "Update Strava" : "Connect Strava"}
                    </Button>
                  </Popover>
                ) : (
                  <Button className="glass-cta" type="default" loading={isConnecting} onClick={connectStrava}>
                    {isStravaConnected ? "Update Strava" : "Connect Strava"}
                  </Button>
                )}
              </div>
            </>
          ),
        },
      ]}
    />
  );
}


